import type { Awareness } from 'y-protocols/awareness';
import { useCursorVisibility } from './presence-indicator';

/** Remote cursors idle longer than this (ms) are hidden automatically. */
export const CURSOR_IDLE_MS = 20_000;

type AwarenessState = Record<string, unknown>;
type AwarenessMeta = Map<number, { lastUpdated: number }>;

interface FilterOptions {
  hiddenIds: Set<string>;
  allHidden: boolean;
  now: number;
  idleMs: number;
}

/**
 * Pure filter: given the full awareness states + meta, drop the remote cursors
 * that should be hidden (in `hiddenIds`, all hidden, or idle past `idleMs`).
 * The local client is NEVER dropped. User identity is `state.user.id`, falling
 * back to `client-<clientId>` (matching AwarenessProvider's fallback).
 *
 * Ported from the legacy CollaborativeEditor `makeProvider.getStates` filter.
 * Difference: Lexical set `focusing = false` to skip rendering; CollaborationCaret
 * renders one caret per state with no per-user skip hook, so we omit the entry
 * entirely instead — the real awareness is untouched, so server sync is
 * unaffected (this only changes what the caret renderer sees).
 */
export function filterCursorStates(
  states: Map<number, AwarenessState>,
  meta: AwarenessMeta,
  localClientId: number,
  { hiddenIds, allHidden, now, idleMs }: FilterOptions,
): Map<number, AwarenessState> {
  const out = new Map<number, AwarenessState>();
  states.forEach((state, clientId) => {
    if (clientId === localClientId) {
      out.set(clientId, state);
      return;
    }
    const userId = (state.user as { id?: string } | undefined)?.id ?? `client-${clientId}`;
    const lastUpdated = meta.get(clientId)?.lastUpdated ?? now;
    const isIdle = now - lastUpdated > idleMs;
    if (isIdle || allHidden || hiddenIds.has(userId)) return; // omit — hidden
    out.set(clientId, state);
  });
  return out;
}

/**
 * Wrap an Awareness so the cursor renderer (CollaborationCaret) only sees
 * un-hidden, non-idle remote users. Everything except `getStates` delegates to
 * the real awareness, so sync/broadcast is unchanged. `getStates` applies
 * {@link filterCursorStates} using the live `useCursorVisibility` store.
 *
 * Pass the result as `CollaborationCaret`'s `provider.awareness`.
 */
export function createFilteredAwareness(awareness: Awareness): Awareness {
  return new Proxy(awareness, {
    get(target, prop, receiver) {
      if (prop === 'getStates') {
        return () => {
          const { hiddenIds, allHidden } = useCursorVisibility.getState();
          return filterCursorStates(
            target.getStates() as Map<number, AwarenessState>,
            target.meta as AwarenessMeta,
            target.clientID,
            { hiddenIds, allHidden, now: Date.now(), idleMs: CURSOR_IDLE_MS },
          );
        };
      }
      const value = Reflect.get(target, prop, receiver);
      // Methods must stay bound to the real awareness (setLocalState,
      // setLocalStateField, on/off, emit, destroy, …) so broadcasting works —
      // the setLocalStateField-is-a-no-op-when-null footgun lives in the real
      // impl, which we deliberately do not shadow.
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}
